import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { CommunityPost } from './CommunityPost';
import { Skeleton } from '../ui/skeleton';
import { useToast } from '../ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

interface Post {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  author: {
    student_name: string;
    avatar_url: string | null;
  };
}

interface Comment {
  id: string;
  content: string;
  created_at: string;
  author: {
    student_name: string;
    avatar_url: string | null;
  };
}

export const CommunityFeed = forwardRef<{ refetch: () => Promise<void> }>((_, ref) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchPosts = async () => {
    try {
      const { data: posts, error } = await supabase
        .from('community_posts')
        .select(`
          *,
          author:profiles!community_posts_user_id_fkey(
            student_name,
            avatar_url
          )
        `)
        .eq('archived', false)  
        .order('created_at', { ascending: false });

      if (error) throw error;

      setPosts(posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las publicaciones',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleComment = async (postId: string, content: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error } = await supabase
        .from('post_comments')
        .insert({
          post_id: postId,
          user_id: user.id,
          content: content.trim(),
        });

      if (error) throw error;

      toast({
        title: "¡Comentario agregado!",
        description: "Tu comentario ha sido publicado.",
      });
    } catch (error) {
      console.error('Error adding comment:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo agregar el comentario",
      });
      throw error;
    }
  };

  const getComments = async (postId: string): Promise<Comment[]> => {
    try {
      const { data, error } = await supabase
        .from('post_comments')
        .select(`
          *,
          author:profiles!post_comments_user_id_fkey(
            student_name,
            avatar_url
          )
        `)
        .eq('post_id', postId)
        .eq('archived', false)  
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching comments:', error);
      return [];
    }
  };

  const handleEdit = async (postId: string, content: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error } = await supabase
        .from('community_posts')
        .update({ content })
        .eq('id', postId)
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: "¡Publicación actualizada!",
        description: "Tu publicación ha sido actualizada exitosamente.",
      });

      await fetchPosts();
      return true;
    } catch (error) {
      console.error('Error editing post:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo actualizar la publicación",
      });
      return false;
    }
  };

  const handleDelete = async (postId: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error } = await supabase
        .from('community_posts')
        .delete()
        .eq('id', postId)
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: "Publicación eliminada",
        description: "Tu publicación ha sido eliminada exitosamente.",
      });

      await fetchPosts();
      return true;
    } catch (error) {
      console.error('Error deleting post:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo eliminar la publicación",
      });
      return false;
    }
  };

  // Exponer la función refetch a través de la ref
  useImperativeHandle(ref, () => ({
    refetch: fetchPosts
  }));

  useEffect(() => {
    fetchPosts();
  }, []);

  return (
    <div className="flex flex-col gap-2 sm:gap-3 md:gap-4">
      {loading ? (
        <div className="space-y-2 sm:space-y-3 md:space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm p-2 sm:p-3 md:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <Skeleton className="h-8 w-8 sm:h-10 sm:w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-24 sm:w-32" />
                  <Skeleton className="h-4 w-full max-w-[300px]" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2 sm:space-y-3 md:space-y-4">
          {posts.map((post) => (
            <CommunityPost
              key={post.id}
              post={post}
              onComment={handleComment}
              getComments={getComments}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onReport={() => {}}
            />
          ))}
          {posts.length === 0 && (
            <div className="text-center py-6 sm:py-8 md:py-10">
              <p className="text-gray-500 text-sm sm:text-base">
                No hay publicaciones aún. ¡Sé el primero en compartir algo!
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
